import React, { useState, useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import bookingService from '../../../../../../services/booking.service';
import { useEyeconxStore } from '../../../../../../store/eyeconxStore';
import { confirmDelete, showError, showSuccess } from '../../../../../../utils/ecxSwal';
import EcxDatePicker from '../../../../../common/EcxDatePicker';
import EcxInlineCalendar from '../../../../../common/EcxInlineCalendar';
import EcxTimePicker from '../../../../../common/EcxTimePicker';
import { formatDisplayDate } from '../../../../../common/ecxDateUtils';
import { formatDisplayTime, normalizeTimeToInterval } from '../../../../../common/ecxTimeUtils';
import { ClinicInfoPanel } from '../../info/clinicInfoParts';
import ClinicSettingsEmpty from '../../subparts/ClinicSettingsEmpty';
import ClinicSettingsCancelButton from '../../subparts/ClinicSettingsCancelButton';
import ClinicSettingsFormFooter from '../../subparts/ClinicSettingsFormFooter';
import ClinicSettingsListSkeleton from '../../subparts/ClinicSettingsListSkeleton';
import Pagination from '../../../../../common/Pagination';
import {
    createEndTimeValidator,
    createLunchFromValidator,
    createLunchToValidator,
} from './doctorScheduleValidation';

const DoctorCustomHours = ({ doctor }) => {
    const clinicId = useEyeconxStore((state) => state.user.clinicId);
    const [loading, setLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [customHours, setCustomHours] = useState([]);
    const [editingId, setEditingId] = useState(null);
    const [addingNew, setAddingNew] = useState(false);
    const [markingDaysOff, setMarkingDaysOff] = useState(false);
    const [selectedOffDates, setSelectedOffDates] = useState([]);
    const [pageNo, setPageNo] = useState(0);
    const [pageSize] = useState(5);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);

    const { register, control, handleSubmit, reset, formState: { errors }, setValue, watch, getValues } = useForm({
        mode: 'onChange',
        defaultValues: {
            isOff: false,
            startTime: '09:00',
            endTime: '17:00',
            lunchFrom: '12:00',
            lunchTo: '13:00',
        },
    });

    const watchIsOff = watch('isOff');
    const isMultiDayOff = (addingNew && watchIsOff) || markingDaysOff;

    useEffect(() => {
        if (doctor?.id) {
            getCustomHours();
        }
    }, [doctor?.id, clinicId, pageNo]);

    const getCustomHours = async () => {
        setLoading(true);
        try {
            const res = await bookingService.getCustomHours(clinicId, doctor.id, pageNo, pageSize);
            setCustomHours(res.data.content || []);
            setTotalPages(res.data.totalPages || 0);
            setTotalElements(res.data.totalElements || 0);
        } catch (error) {
            console.log('Error fetching custom hours:', error);
            setCustomHours([]);
        } finally {
            setLoading(false);
        }
    };

    const buildCustomHourPayload = (workDate, data, id) => ({
        ...(id ? { id } : {}),
        doctorId: doctor.id,
        workDate,
        isOff: data.isOff,
        startTime: data.isOff ? null : `${data.startTime}:00`,
        endTime: data.isOff ? null : `${data.endTime}:00`,
        lunchFrom: data.isOff ? null : `${data.lunchFrom}:00`,
        lunchTo: data.isOff ? null : `${data.lunchTo}:00`,
        userId: doctor.userId,
        clinicId,
    });

    const addCustomHours = async (data) => {
        if (isMultiDayOff) {
            if (selectedOffDates.length === 0) {
                showError({
                    title: 'No Dates Selected',
                    text: 'Select at least one day to mark as off.',
                });
                return;
            }

            setIsSaving(true);
            try {
                for (const workDate of selectedOffDates) {
                    await bookingService.addCustomHour(
                        clinicId,
                        doctor.id,
                        buildCustomHourPayload(workDate, { ...data, isOff: true })
                    );
                }
                await getCustomHours();
                handleCancel();
                await showSuccess({
                    title: 'Days Off Saved',
                    text:
                        selectedOffDates.length === 1
                            ? '1 day was marked as off.'
                            : `${selectedOffDates.length} days were marked as off.`,
                });
            } catch (error) {
                console.log('Error marking days off:', error);
                showError({
                    title: 'Save Failed',
                    text: 'Unable to save one or more day-off dates. Please try again.',
                });
            } finally {
                setIsSaving(false);
            }
            return;
        }

        setIsSaving(true);
        try {
            await bookingService.addCustomHour(
                clinicId,
                doctor.id,
                buildCustomHourPayload(data.workDate, data)
            );
            await getCustomHours();
            handleCancel();
            await showSuccess({
                title: 'Custom Hours Added',
                text: 'The custom schedule was added successfully.',
            });
        } catch (error) {
            console.log('Error adding custom hour:', error);
            showError({
                title: 'Add Failed',
                text: 'Unable to add custom hours. Please try again.',
            });
        } finally {
            setIsSaving(false);
        }
    };

    const updateCustomHours = async (id, data) => {
        setIsSaving(true);
        try {
            await bookingService.addCustomHour(
                clinicId,
                doctor.id,
                buildCustomHourPayload(data.workDate, data, id)
            );
            await getCustomHours();
            handleCancel();
            await showSuccess({
                title: 'Custom Hours Updated',
                text: 'The custom schedule was updated successfully.',
            });
        } catch (error) {
            console.log('Error updating custom hour:', error);
            showError({
                title: 'Update Failed',
                text: 'Unable to update custom hours. Please try again.',
            });
        } finally {
            setIsSaving(false);
        }
    };

    const deleteCustomHours = async (customHour) => {
        const result = await confirmDelete({
            title: 'Delete Custom Hours?',
            itemName: formatDate(customHour.workDate),
            description: 'This custom schedule override will be removed.',
            onConfirm: () => bookingService.deleteCustomHour(clinicId, doctor.id, customHour.id),
        });

        if (result.isConfirmed) {
            await getCustomHours();
        }
    };

    const handleEdit = (customHour) => {
        setEditingId(customHour.id);
        setAddingNew(false);
        setMarkingDaysOff(false);
        setSelectedOffDates([]);
        setValue('workDate', customHour.workDate?.substring(0, 10) || '');
        setValue('isOff', customHour.isOff);
        setValue('startTime', normalizeTimeToInterval(customHour.startTime) || '09:00');
        setValue('endTime', normalizeTimeToInterval(customHour.endTime) || '17:00');
        setValue('lunchFrom', normalizeTimeToInterval(customHour.lunchFrom) || '12:00');
        setValue('lunchTo', normalizeTimeToInterval(customHour.lunchTo) || '13:00');
    };

    const handleCancel = () => {
        setEditingId(null);
        setAddingNew(false);
        setMarkingDaysOff(false);
        setSelectedOffDates([]);
        reset({
            workDate: '',
            isOff: false,
            startTime: '09:00',
            endTime: '17:00',
            lunchFrom: '12:00',
            lunchTo: '13:00',
        });
    };

    const handleAddNew = () => {
        setAddingNew(true);
        setEditingId(null);
        setMarkingDaysOff(false);
        setSelectedOffDates([]);
        reset({
            workDate: '',
            isOff: false,
            startTime: '09:00',
            endTime: '17:00',
            lunchFrom: '12:00',
            lunchTo: '13:00',
        });
    };

    const handleMarkDaysOff = () => {
        setMarkingDaysOff(true);
        setAddingNew(true);
        setEditingId(null);
        setSelectedOffDates([]);
        reset({
            workDate: '',
            isOff: true,
            startTime: '09:00',
            endTime: '17:00',
            lunchFrom: '12:00',
            lunchTo: '13:00',
        });
    };

    const handleDayOffToggle = (event) => {
        const checked = event.target.checked;

        if (!addingNew || editingId) {
            return;
        }

        if (checked) {
            setMarkingDaysOff(true);
            const currentDate = getValues('workDate');
            setSelectedOffDates(currentDate ? [currentDate] : []);
            setValue('workDate', '');
        } else {
            setMarkingDaysOff(false);
            const first = selectedOffDates[0] || '';
            setSelectedOffDates([]);
            setValue('workDate', first);
        }
    };

    const removeSelectedOffDate = (dateToRemove) => {
        setSelectedOffDates((prev) => prev.filter((date) => date !== dateToRemove));
    };

    const formatDate = (dateString) =>
        new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            weekday: 'short',
        });

    const formatTime = (timeString) => {
        if (!timeString) return '—';
        return formatDisplayTime(timeString) || normalizeTimeToInterval(timeString);
    };

    const renderCustomHourForm = (onSubmit, submitLabel) => (
        <form onSubmit={handleSubmit(onSubmit)} className="clinic-settings-record-form">
            {isMultiDayOff ? (
                <div className="doctor-custom-hours-multi">
                    <div className="doctor-custom-hours-multi__hint">
                        <i className="bi bi-info-circle me-1"></i>
                        Click multiple dates to mark them as days off. Click a selected date again to remove it.
                    </div>
                    <div className="d-flex align-items-center justify-content-between gap-2 flex-wrap mb-2">
                        <label className="form-label small fw-normal text-muted mb-0">
                            Select Days Off
                            {selectedOffDates.length > 0 && (
                                <span className="ms-1">({selectedOffDates.length} selected)</span>
                            )}
                        </label>
                        <div className="form-check form-switch mb-0">
                            <input
                                className="form-check-input"
                                type="checkbox"
                                {...register('isOff', { onChange: handleDayOffToggle })}
                                disabled={isSaving}
                            />
                            <label className="form-check-label small">Day off</label>
                        </div>
                    </div>
                    <EcxInlineCalendar
                        multiSelect
                        value={selectedOffDates}
                        onChange={setSelectedOffDates}
                        disabled={isSaving}
                    />
                    {selectedOffDates.length > 0 && (
                        <div className="doctor-custom-hours-selected-dates">
                            {selectedOffDates.map((date) => (
                                <button
                                    key={date}
                                    type="button"
                                    className="doctor-custom-hours-selected-date-chip"
                                    onClick={() => removeSelectedOffDate(date)}
                                    disabled={isSaving}
                                    title="Remove date"
                                >
                                    <span>{formatDisplayDate(date)}</span>
                                    <i className="bi bi-x"></i>
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            ) : (
                <>
                    <div className="clinic-settings-record-form__row">
                        <div>
                            <label className="form-label small fw-normal text-muted">Date</label>
                            <Controller
                                name="workDate"
                                control={control}
                                rules={{ required: 'Date is required' }}
                                render={({ field }) => (
                                    <EcxDatePicker
                                        value={field.value}
                                        onChange={field.onChange}
                                        placeholder="Select date"
                                        disabled={isSaving}
                                    />
                                )}
                            />
                            {errors.workDate && <div className="text-danger small mt-1">{errors.workDate.message}</div>}
                        </div>
                        <div className="d-flex align-items-end">
                            <div className="form-check form-switch">
                                <input
                                    className="form-check-input"
                                    type="checkbox"
                                    {...register('isOff', { onChange: handleDayOffToggle })}
                                    disabled={isSaving}
                                />
                                <label className="form-check-label small">Day off</label>
                            </div>
                        </div>
                    </div>
                    {!watchIsOff && (
                        <>
                            <div className="clinic-settings-record-form__row">
                                <div>
                                    <label className="form-label small fw-normal text-muted">Start</label>
                                    <Controller
                                        name="startTime"
                                        control={control}
                                        rules={{ required: watchIsOff ? false : 'Start time is required' }}
                                        render={({ field }) => (
                                            <EcxTimePicker
                                                value={field.value}
                                                onChange={field.onChange}
                                                placeholder="Select start time"
                                            />
                                        )}
                                    />
                                    {errors.startTime && (
                                        <div className="text-danger small mt-1">{errors.startTime.message}</div>
                                    )}
                                </div>
                                <div>
                                    <label className="form-label small fw-normal text-muted">End</label>
                                    <Controller
                                        name="endTime"
                                        control={control}
                                        rules={{
                                            required: watchIsOff ? false : 'End time is required',
                                            validate: createEndTimeValidator(getValues),
                                        }}
                                        render={({ field }) => (
                                            <EcxTimePicker
                                                value={field.value}
                                                onChange={field.onChange}
                                                placeholder="Select end time"
                                            />
                                        )}
                                    />
                                    {errors.endTime && (
                                        <div className="text-danger small mt-1">{errors.endTime.message}</div>
                                    )}
                                </div>
                            </div>
                            <div className="clinic-settings-record-form__row">
                                <div>
                                    <label className="form-label small fw-normal text-muted">Lunch from</label>
                                    <Controller
                                        name="lunchFrom"
                                        control={control}
                                        rules={{ validate: createLunchFromValidator(getValues) }}
                                        render={({ field }) => (
                                            <EcxTimePicker
                                                value={field.value}
                                                onChange={field.onChange}
                                                placeholder="Select lunch start"
                                            />
                                        )}
                                    />
                                    {errors.lunchFrom && (
                                        <div className="text-danger small mt-1">{errors.lunchFrom.message}</div>
                                    )}
                                </div>
                                <div>
                                    <label className="form-label small fw-normal text-muted">Lunch to</label>
                                    <Controller
                                        name="lunchTo"
                                        control={control}
                                        rules={{ validate: createLunchToValidator(getValues) }}
                                        render={({ field }) => (
                                            <EcxTimePicker
                                                value={field.value}
                                                onChange={field.onChange}
                                                placeholder="Select lunch end"
                                            />
                                        )}
                                    />
                                    {errors.lunchTo && (
                                        <div className="text-danger small mt-1">{errors.lunchTo.message}</div>
                                    )}
                                </div>
                            </div>
                        </>
                    )}
                </>
            )}
            <ClinicSettingsFormFooter
                isSaving={isSaving}
                submitLabel={
                    isMultiDayOff
                        ? `Save ${selectedOffDates.length} Day${selectedOffDates.length === 1 ? '' : 's'} Off`
                        : submitLabel
                }
            />
        </form>
    );

    const panelActions = !loading ? (
        addingNew || editingId || markingDaysOff ? (
            <ClinicSettingsCancelButton onClick={handleCancel} disabled={isSaving} />
        ) : (
            <div className="d-flex flex-wrap gap-2">
                <button type="button" className="btn btn-sm btn-light-secondary rounded-pill" onClick={handleMarkDaysOff}>
                    <i className="bi bi-calendar-x me-1"></i>
                    Mark Days Off
                </button>
                <button type="button" className="btn btn-sm btn-light-primary rounded-pill" onClick={handleAddNew}>
                    <i className="bi bi-plus-circle me-1"></i>
                    Add Custom Hours
                </button>
            </div>
        )
    ) : null;

    if (loading && customHours.length === 0) {
        return (
            <ClinicInfoPanel title="Custom Hours" icon="bi-calendar-event">
                <ClinicSettingsListSkeleton rows={3} />
            </ClinicInfoPanel>
        );
    }

    return (
        <ClinicInfoPanel title="Custom Hours" icon="bi-calendar-event" actions={panelActions}>

            {addingNew && renderCustomHourForm(addCustomHours, 'Add')}

            {customHours.length === 0 && !addingNew ? (
                <ClinicSettingsEmpty
                    icon="bi-calendar-x"
                    title="No custom hours"
                    description="Add custom hours to override the regular weekly schedule."
                    actionLabel="Add Custom Hours"
                    onAction={handleAddNew}
                />
            ) : (
                <>
                    <div className="clinic-settings-record-list">
                        {customHours.map((customHour) => (
                            <div
                                key={customHour.id}
                                className={`clinic-settings-record-item ${editingId === customHour.id ? 'clinic-settings-record-item--editing' : ''}`}
                            >
                                {editingId === customHour.id ? (
                                    <div className="flex-grow-1">
                                        {renderCustomHourForm((data) => updateCustomHours(customHour.id, data), 'Save')}
                                    </div>
                                ) : (
                                    <>
                                        <span className="clinic-settings-record-item__avatar">
                                            <i className="bi bi-calendar-event"></i>
                                        </span>
                                        <div className="clinic-settings-record-item__main">
                                            <h6 className="clinic-settings-record-item__title">
                                                {formatDate(customHour.workDate)}
                                            </h6>
                                            <p className="clinic-settings-record-item__meta mb-0">
                                                {customHour.isOff
                                                    ? 'Day off'
                                                    : `${formatTime(customHour.startTime)} – ${formatTime(customHour.endTime)} · Lunch ${formatTime(customHour.lunchFrom)} – ${formatTime(customHour.lunchTo)}`}
                                            </p>
                                        </div>
                                        <span className={`clinic-settings-status-pill ${customHour.isOff ? 'is-closed' : 'is-open'}`}>
                                            {customHour.isOff ? 'Day Off' : 'Working'}
                                        </span>
                                        <div className="clinic-settings-record-item__actions">
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-outline-secondary rounded-circle"
                                                onClick={() => handleEdit(customHour)}
                                                title="Edit"
                                            >
                                                <i className="bi bi-pencil"></i>
                                            </button>
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-outline-danger rounded-circle"
                                                onClick={() => deleteCustomHours(customHour)}
                                                title="Delete"
                                            >
                                                <i className="bi bi-trash"></i>
                                            </button>
                                        </div>
                                    </>
                                )}
                            </div>
                        ))}
                    </div>

                    {totalPages > 1 && (
                        <div className="doctor-custom-hours__pagination">
                            <span className="text-muted small">
                                Showing {customHours.length} of {totalElements}
                            </span>
                            <Pagination
                                currentPage={pageNo}
                                totalPages={totalPages}
                                onPageChange={setPageNo}
                            />
                        </div>
                    )}
                </>
            )}
        </ClinicInfoPanel>
    );
};

export default DoctorCustomHours;
